import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarCheck, CalendarDays, Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const CITIES = [
  "Delhi NCR", "Mumbai", "Bengaluru", "Pune", "Hyderabad", "Chennai",
  "Kolkata", "Ahmedabad", "Jaipur", "Chandigarh", "Indore", "Kochi",
];
const SLOTS = ["10:00 AM", "11:30 AM", "1:00 PM", "2:30 PM", "4:00 PM", "5:30 PM"];

function nextDays(count) {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const d = new Date(now);
    d.setDate(now.getDate() + i);
    out.push({
      iso: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }),
    });
  }
  return out;
}

export default function TestDriveScheduler({ booking, onScheduled }) {
  const [city, setCity] = useState(null);
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState(null);
  const [avail, setAvail] = useState(null);
  const [saving, setSaving] = useState(false);
  const days = nextDays(7);

  useEffect(() => {
    if (!date) return;
    setAvail(null);
    setSlot(null);
    api
      .get(`/test-drive/availability?date=${date}`)
      .then((r) => setAvail(r.data))
      .catch(() => toast.error("Could not load slots. Try again."));
  }, [date]);

  const isTaken = (s, c) =>
    Boolean(avail?.slots?.find((x) => x.slot === s)?.cities?.find((y) => y.city === c)?.booked);

  const schedule = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/bookings/${booking.id}/test-drive`, { city, date, slot });
      onScheduled(res.data);
      toast.success("Test drive booked — confirmation sent to your email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book the slot. Try again.");
      setSaving(false);
    }
  };

  const chip = (active, disabled) =>
    `rounded-full border px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.14em] transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-35 ${
      active
        ? "border-[#e6b800] bg-[#e6b800] font-semibold text-[#070709]"
        : "border-white/15 text-neutral-300 hover:border-[#e6b800]/50"
    }`;

  if (booking.test_drive) {
    const td = booking.test_drive;
    return (
      <div
        className="mt-6 rounded-2xl border border-[#e6b800]/30 bg-[#e6b800]/5 p-6 sm:p-8"
        data-testid="test-drive-booked-card"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#e6b800] text-[#070709]">
          <CalendarCheck size={20} />
        </span>
        <h3 className="mt-4 font-display text-xl font-bold uppercase text-white">Test drive booked</h3>
        <p className="mt-2 text-sm leading-relaxed text-neutral-300">
          {td.city} · {new Date(td.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })} · {td.slot}
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          Details sent to {booking.email}. Reach the IshuCar experience centre 10 minutes early — our team will be waiting with your IshuCar.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#111116] p-6 sm:p-8" data-testid="test-drive-scheduler">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
          <CalendarDays size={20} />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold uppercase text-white">Schedule your test drive</h3>
          <p className="text-xs text-neutral-500">Optional — free with your reservation, anytime refundable.</p>
        </div>
      </div>

      <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
        1 · City <MapPin size={11} className="ml-1 inline text-[#e6b800]" />
      </p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="test-drive-city-picker">
        {CITIES.map((c) => (
          <button key={c} data-testid={`test-drive-city-${c.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setCity(c)} className={chip(city === c, false)}>
            {c}
          </button>
        ))}
      </div>

      <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">2 · Date</p>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="test-drive-date-picker">
        {days.map((d, i) => (
          <button key={d.iso} data-testid={`test-drive-date-${i}`} onClick={() => setDate(d.iso)} className={chip(date === d.iso, false)}>
            {d.label}
          </button>
        ))}
      </div>

      <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">3 · Time slot</p>
      {!date && <p className="mt-3 text-xs text-neutral-600">Pick a date first to see open slots.</p>}
      {date && !avail && <Loader2 size={16} className="mt-3 animate-spin text-neutral-500" />}
      {avail && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="test-drive-slot-picker">
          {SLOTS.map((s, i) => (
            <button
              key={s}
              data-testid={`test-drive-slot-${i}`}
              onClick={() => setSlot(s)}
              disabled={!city || isTaken(s, city)}
              className={chip(slot === s, !city || isTaken(s, city))}
              title={!city ? "Pick a city first" : isTaken(s, city) ? "Already booked" : ""}
            >
              {s}{isTaken(s, city) ? " · full" : ""}
            </button>
          ))}
        </div>
      )}

      <div className="mt-7 flex flex-wrap items-center gap-4">
        <button
          onClick={schedule}
          data-testid="test-drive-book-button"
          disabled={!city || !date || !slot || saving}
          className="inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-6 py-3 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CalendarCheck size={15} />}
          {saving ? "Booking…" : "Book test drive"}
        </button>
        <span className="text-xs text-neutral-600">Confirmation goes to {booking.email}</span>
      </div>
    </div>
  );
}